import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { createUseStyles } from "$utils/createUseStyles";
import clsx from "clsx";
import dateFormat from "dateformat";
import { Backdrop, CircularProgress } from "@mui/material";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import { theme } from "$utils/theme/bataillog";

import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import usePublicShippingRecord from "$hooks/usePublicShippingRecord";

import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { getShippingRecordStatusLabelByRoleAndStatus } from "$utils/typeConstants";
import { DeliveryType, ShippingRecordStatus } from "$types";

import { Layout, Text } from "$components";
import ShippingDeliveryDatesBlock from "$components/ShippingRecord/ShippingDeliveryDatesBlock";
import ShippingLocationInfoBlock from "$components/ShippingRecord/ShippingLocationInfoBlock";
import ShippingParcelInfoBlock from "$components/ShippingRecord/ShippingParcelInfoBlock";
import ShippingRecordFileUploadBlock from "$components/ShippingRecord/ShippingRecordFileUploadBlock";
import ShippingRecordDeliveryTrackingBlock from "$components/ShippingRecord/ShippingRecordDeliveryTrackingBlock";
import GenericStatusHistoryButton from "$components/GenericStatusHistoryButton";
import ShippingRecordPublicInfoBlock from "$components/ShippingRecord/ShippingRecordPublicInfoBlock";
import ShippingRecordShareButton from "./ShippingRecordShareButton";

function ShippingPublicPage() {
  const { id: shippingRecordId } = useParams<any>();
  const { userRole } = useAuth();

  const { shippingRecord, isFetchingShippingRecord, isNotFound, fetchError } =
    usePublicShippingRecord(shippingRecordId);

  const isShippingRecordCanceled =
    shippingRecord?.status === ShippingRecordStatus.CANCELED;
  const isLinkedToExternalConnector = shippingRecord?.shipments?.some(
    (shipment) => shipment.connectorOrderId
  );

  const jclasses = useJStyles();
  const classes = useStyles();

  const statusHistory = useMemo(
    () =>
      Object.keys(ShippingRecordStatus).map((statusKey) => {
        const statusHistory = shippingRecord?.statusHistory.find(
          (history) => history.status === ShippingRecordStatus[statusKey]
        );
        const date =
          statusKey === ShippingRecordStatus.TO_SHIP
            ? statusHistory?.date ||
              statusHistory?.updatedAt ||
              statusHistory?.createdAt
            : statusHistory?.date;
        return {
          value: ShippingRecordStatus[statusKey],
          label: getShippingRecordStatusLabelByRoleAndStatus(
            userRole,
            ShippingRecordStatus[statusKey]
          ),
          date,
        };
      }),
    [shippingRecord?.statusHistory, userRole]
  );

  const renderHead = () => (
    <div className={classes.headerContainer}>
      <Text type="h2" className={classes.title}>
        {shippingRecord?.code}
      </Text>
      <div className={classes.customHeader}>
        <div className={classes.calendar}>
          <CalendarCheckFillIcon className={classes.calendarIcon} />
          Créé le {dateFormat(shippingRecord?.createdAt, DEFAULT_DATE_FORMAT)}
        </div>
        <div className={classes.badge}>
          <GenericStatusHistoryButton
            disabled
            isFetching={isFetchingShippingRecord}
            currentStatus={shippingRecord?.status}
            statusHistory={statusHistory}
            hiddenValues={[
              ShippingRecordStatus.CANCELED,
              ShippingRecordStatus.DRAFT,
            ]}
            lockedValues={
              isLinkedToExternalConnector
                ? Object.keys(ShippingRecordStatus)
                : [ShippingRecordStatus.CANCELED, ShippingRecordStatus.DRAFT]
            }
            updateStatus={null}
          />
        </div>
      </div>
      <div className={classes.lineBlockContainer}>
        <div className={classes.cancelButtonContainer}>
          <ShippingRecordShareButton />
        </div>
        <div className={classes.cancelButtonContainer}>
          {isShippingRecordCanceled && (
            <Text type="disabled">{`Dossier annulé le ${dateFormat(
              shippingRecord?.statusHistory?.find(
                (history) => history.status === ShippingRecordStatus.CANCELED
              )?.updatedAt,
              DEFAULT_DATE_FORMAT
            )}`}</Text>
          )}
        </div>
      </div>
    </div>
  );

  if (!isFetchingShippingRecord && !shippingRecord?.id) {
    return (
      <Layout isPublic>
        <div className={clsx(jclasses.jWrapper, classes.mainContainer)}>
          <Text type="h2">
            {isNotFound ? "Dossier introuvable" : "Erreur de chargement"}
          </Text>
          <Text type="text" style={{ marginTop: 16 }}>
            {fetchError ??
              (isNotFound
                ? "Ce lien de suivi n'est pas valide ou le dossier n'existe plus."
                : "Impossible de charger le dossier de transport. Veuillez réessayer plus tard.")}
          </Text>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isPublic>
      <div className={clsx(jclasses.jWrapper, classes.mainContainer)}>
        <div>
          {renderHead()}
          <div className={classes.shippingLocationRow}>
            <ShippingDeliveryDatesBlock
              shippingRecord={shippingRecord}
              updateShippingRecord={() => {}}
              disabled
              classes={classes}
            />
            <ShippingLocationInfoBlock
              shippingRecord={shippingRecord}
              classes={classes}
            />
          </div>
          <ShippingParcelInfoBlock
            shippingRecord={shippingRecord}
            classes={classes}
            isPublic
          />
          <div className={classes.lineBlockContainer}>
            <ShippingRecordPublicInfoBlock
              shippingRecord={shippingRecord || {}}
              classes={classes}
            />
            <div className={classes.lineBlockChildContainer}>
              <ShippingRecordFileUploadBlock
                shippingRecord={shippingRecord}
                classes={classes}
                updateShippingRecord={() => {}}
                fetchShippingRecord={() => {}}
                disabled
              />
            </div>
          </div>
          {shippingRecord &&
            shippingRecord.deliveryType !== DeliveryType.PROVISIONING && (
              <ShippingRecordDeliveryTrackingBlock
                shippingRecord={shippingRecord}
                classes={classes}
                fetchShippingRecord={() => {}}
                disabled
                isPublic
              />
            )}
        </div>
        <Backdrop
          style={{
            zIndex: 9999,
            color: "white",
          }}
          open={isFetchingShippingRecord}
          onClick={() => {}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </Layout>
  );
}

export default ShippingPublicPage;

const useStyles = createUseStyles(() => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    padding: 20,
    background: theme.color.gray1,
    flex: 1,
  },
  title: {
    marginTop: 15,
  },
  lineContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  lineBlockContainer: {
    display: "flex",
  },
  shippingLocationRow: {
    display: "grid",
    gridTemplateColumns: "1fr 3fr",
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4),
    width: "100%",
    alignItems: "stretch",
  },
  shippingDeliveryDatesPaper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 0,
    minWidth: 0,
  },
  shippingLocationInfoPaper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 0,
    minWidth: 0,
  },
  lineBlockChildContainer: {
    display: "flex",
    flexGrow: 1,
    marginLeft: theme.spacing(4),
    overflow: "auto",
  },
  priceContainer: {
    display: "flex",
    alignItems: "center",
    width: 300,
    "& > div": {
      flex: 1,
    },
  },
  inputContainer: {
    display: "flex",
    marginRight: theme.spacing(4),
    width: 200,
    "&.MuiFormControl-root": {
      width: "100% !important",
    },
  },
  datePickerPopper: {
    zIndex: 9999,
  },
  selectContainer: {
    display: "flex",
    flex: 1,
  },
  paperContainer: {
    display: "flex",
    flex: "none",
    flexGrow: 1,
    flexDirection: "column",
    marginBottom: theme.spacing(4),
  },
  spinnerContainer: {
    display: "flex",
    flex: 1,
    alignSelf: "center",
    justifyContent: "center",
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    "& h3": {
      margin: `0 ${theme.spacing(1)}px`,
    },
    zIndex: 0,
  },
  subTitle: {
    margin: "0 !important",
    fontWeight: "bold",
    fontSize: "14px !important",
  },
  calendarIcon: {
    width: 18,
    marginRight: theme.spacing(1),
  },
  buttonHeader: {
    alignSelf: "flex-end",
  },
  badge: {
    marginRight: theme.spacing(2),
    "& span": {
      fontSize: 14,
      padding: `0 ${theme.spacing(2)}px`,
    },
  },
  headerContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "space-between",
  },
  customHeader: {
    display: "flex",
    marginLeft: theme.spacing(2),
    alignItems: "center",
    flex: 1,
  },
  calendar: {
    display: "flex",
    padding: `0 ${theme.spacing(2)}px`,
    alignItems: "center",
    borderLeft: `1px solid ${theme.color.gray2}`,
    borderRight: `1px solid ${theme.color.gray2}`,
    color: theme.color.gray5,
    fontSize: 14,
    marginRight: theme.spacing(2),
  },
  boldBlueText: {
    color: theme.color.blue,
    fontWeight: "bold",
  },
  tableHead: {
    borderRadius: "3px 3px 0px 0px",
    backgroundColor: theme.color.gray1,
  },
  opTableHeadCell: {
    "&.MuiTableCell-head": {
      color: theme.color.gray5,
      fontFamily: "Work Sans, sans-serif",
      fontSize: 13,
      textTransform: "uppercase",
      padding: "12px 12px 10px 12px !important",
    },
  },
  parcelTableContainer: {
    marginBottom: theme.spacing(4),
  },
  totalParcelContainer: {
    display: "flex",
    flexGrow: 1,
  },
  totalParcelTitle: {
    flex: 1,
    fontWeight: "bold",
  },
  totalParcelTable: {
    flex: 6,
  },
  addButtonContainer: {
    width: 20,
    marginLeft: theme.spacing(2),
  },
  addButton: {
    maxWidth: 20,
    minWidth: `20px !important`,
    padding: `0px !important`,
    height: "100%",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
  cancelButtonContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(2),
  },
  cell: {
    fontSize: 15,
  },
  subRow: {
    cursor: "default",
  },
}));
